import cn from 'classnames'
import useTranslation from 'next-translate/useTranslation'
import getConfig from 'next/config'
import Image from 'next/image'
import React from 'react'

import { FavouriteButton } from '@/components/FavouriteButton'
import { ContextValue } from '@/context/Cities'
import { useUserNavigation } from '@/context/UserNavigationProvider'
import ArrowLeft from '@/design-lib/components/Icons/ArrowLeft'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { theme } from '@/design-lib/style-theme/theme.css'
import { ListingCate } from '@/page-commons/listingBySlug'
import { NearbyProperty } from '@/utils/nearby-properties-helper'
import { handleListingDetailClick } from '@/utils/tracking/eventsHandlers'

import MobileFloatingButtons from './MobileFloatingButtons'
import { NearbyBanner } from './NearbyBanner'
import { NearbyNavigation } from './NearbyNavigation'
import {
  panelLeft,
  panelRight,
  centralPanel,
  navContainer,
  sidePanel,
  innerWrapper,
  mobileHidden,
  mobileHeader,
  outerWrapper,
} from './styles.css'

type Props = {
  cities: ContextValue
  isSticky: boolean
  listing: ListingCate
  nearbyProperties: NearbyProperty[]
  setShowNavigation: (v: boolean) => void
  showBanner: boolean
  showNavigation: boolean
}

export const getPreviousUrl = (nearbyProperties: NearbyProperty[], listing: ListingCate): string => {
  if (!nearbyProperties.length) return '#'

  const currentIndex: number = nearbyProperties.findIndex((np: NearbyProperty) => np.id === listing.id)

  return nearbyProperties[currentIndex - 1]?.url || nearbyProperties[nearbyProperties.length - 1].url
}

export const getNextUrl = (nearbyProperties: NearbyProperty[], listing: ListingCate): string => {
  if (!nearbyProperties.length) return '#'

  const currentIndex: number = nearbyProperties.findIndex((np: NearbyProperty) => np.id === listing.id)
  return nearbyProperties[currentIndex + 1]?.url || nearbyProperties[0].url
}

export const NearbyPropertiesSection: React.FC<Props> = ({
  cities,
  isSticky,
  listing,
  nearbyProperties,
  setShowNavigation,
  showBanner,
  showNavigation,
}) => {
  const { t } = useTranslation()

  const { currentPage, returnToLastCityPage, setLastCityFromListing } = useUserNavigation()

  if (cities && !currentPage && !!cities.router.city?.slug) {
    setLastCityFromListing(cities.router.city.slug)
  }

  const totalProperties = nearbyProperties.length

  const currentIndex: number = nearbyProperties.findIndex((np: NearbyProperty) => np.id === listing.id)

  const previousUrl: string = getPreviousUrl(nearbyProperties, listing)
  const nextUrl: string = getNextUrl(nearbyProperties, listing)

  const showComponentInMobile: boolean = showBanner || showNavigation

  const progressPercentage: number = Math.floor(((currentIndex + 1) / totalProperties) * 100)

  const progressStyle = {
    backgroundImage: `linear-gradient(90deg, ${theme.colors.orange600} ${progressPercentage}%, ${theme.colors.greyscale200} ${progressPercentage}%)`,
    backgroundPosition: 'bottom',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100% 2px',
  }

  const {
    publicRuntimeConfig: { casavoUiCdn },
  } = getConfig()

  return (
    <React.Fragment>
      <div className={mobileHeader}>
        <button
          aria-label={'Return to city page'}
          className={sprinkles({
            alignItems: 'center',
            display: 'flex',
          })}
          onClick={() => {
            returnToLastCityPage()
          }}
        >
          <Image alt="" height={30} src={`${casavoUiCdn}/logo/main-wordmark-black-mobile.svg`} width={110} />
        </button>
      </div>
      <div className={outerWrapper[isSticky ? 'sticky' : 'default']}>
        <div
          className={showComponentInMobile ? innerWrapper : cn(innerWrapper, mobileHidden)}
          data-testid="navigate-and-favorite-bar"
          style={showNavigation ? progressStyle : {}}
        >
          <nav className={navContainer}>
            <div className={sidePanel}>
              <div className={panelRight}>
                <button
                  aria-label="Go back to list"
                  className={sprinkles({
                    alignItems: 'center',
                    display: 'flex',
                    gap: 's',
                  })}
                  data-testid="detail-back-button-desktop"
                  tabIndex={0}
                  onClick={() => {
                    handleListingDetailClick('GoBack')
                    returnToLastCityPage()
                  }}
                >
                  <ArrowLeft height={16} width={16} />
                  <span
                    className={sprinkles({
                      paddingLeft: 's',
                    })}
                  >
                    {t('listing-detail:properties')}
                  </span>
                </button>
              </div>
            </div>
            <div className={centralPanel}>
              {showNavigation && (
                <NearbyNavigation
                  currentIndex={currentIndex}
                  nextUrl={nextUrl}
                  previousUrl={previousUrl}
                  totalProperties={totalProperties}
                />
              )}
              {showBanner && (
                <NearbyBanner
                  nextUrl={nextUrl}
                  totalProperties={totalProperties}
                  onCTAClick={() => {
                    setShowNavigation(true)
                  }}
                />
              )}
            </div>
            <div className={sidePanel}>
              <div className={panelLeft}>
                <FavouriteButton extended gtmTouchpoint="ListingDetail" id={listing.id} />
              </div>
            </div>
          </nav>
        </div>
      </div>
      <MobileFloatingButtons cities={cities} listing={listing} topOffset={showComponentInMobile} />
    </React.Fragment>
  )
}
